import { useEffect, useState } from "react";
import { Card, SectionTitle } from "../components/ui";
import type { Api } from "../lib/api";

/**
 * FAQ — the single customer-facing self-help home. It absorbs the former "help articles"
 * (which used to live on the Documents tab) so the FAQ is the FAQ and the Documents tab is
 * purely the document library. Content is curated, non-CUI static text hardcoded here and
 * rendered as plain React text, so answers are always escaped and there is no HTML/XSS
 * surface. Each entry is a collapsible <details> disclosure for quick scanning, and each
 * carries a stable id so the pre-case deflection panel can deep-link to it (/app/faq#id).
 */
export interface FaqItem {
  id: string;
  question: string;
  /** Plain-text answer paragraphs, rendered escaped by React. */
  answer: string[];
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: "email-notifications",
    question: "Will I get an email when my support case is updated?",
    answer: [
      "Yes. When PumaMesh replies to one of your cases, changes its status, or resolves it, we email the person who opened the case plus anyone added as a watcher on it. Anyone @mentioned in a reply also gets an email.",
      "Notifications come from support@pumamesh.com. If you are not seeing them, check your spam folder and add that address to your contacts, then confirm your email is correct on your account.",
      "You can fine-tune or opt out of notifications from the Tickets tab under notification preferences.",
    ],
  },
  {
    id: "mentions",
    question: "How do I notify a specific person on a case? (@mentions)",
    answer: [
      "In any reply or internal note, type @ followed by a name and pick the person from the dropdown. They will get an email letting them know they were mentioned.",
      "You can only mention teammates on your own account and PumaMesh staff. For privacy, you can never see or notify users who belong to a different account.",
    ],
  },
  {
    id: "download-builds",
    question: "How do I download and verify a PumaMesh build?",
    answer: [
      "Open the Builds tab and download both matching files from release-rpm-el10: pumamesh-portal-<version>-<build>.avx256.el10.x86_64.rpm and pumamesh-portal-selinux-<version>-<build>.avx256.el10.noarch.rpm. The release-avx256-rl10 tarball remains a separate download. Each link is short-lived; click Download again if it expires.",
      "Compute SHA-256 separately for both files with sha256sum and compare each digest to its value on the Builds tab. If either differs, delete it and download again. Confirm both package signatures before installing.",
    ],
  },
  {
    id: "signing-verify",
    question: "How do I confirm a build is genuinely signed by PumaMesh?",
    answer: [
      "The SHA-256 checksum proves the file arrived intact; the package signature proves PumaMesh actually built and signed it. Do both before installing. Every published RPM is signed with the PumaMesh package-signing key (key id 34e8f87c).",
      "First import the signing key: sudo rpm --import https://packages.pumamesh.com/RPM-GPG-KEY-pumamesh. Then check both files individually: rpm --checksig pumamesh-portal-*.avx256.el10.x86_64.rpm and rpm --checksig pumamesh-portal-selinux-*.avx256.el10.noarch.rpm — each trusted package reports “digests signatures OK”.",
      "If you see NOKEY, the key is not imported yet — repeat the import step. If you see NOT OK or a missing PGP line, do not install it: delete the file and re-download it from the Builds tab.",
    ],
  },
  {
    id: "install-rl10",
    question: "How do I install PumaMesh on Rocky Linux 10?",
    answer: [
      "Use the .el10 RPM on Rocky Linux 10 (it also runs on RHEL / AlmaLinux 10). Rocky Linux 9 is retired and no longer published. The AVX-256 build targets modern x86-64 server CPUs, and you need root / sudo. The agent bundles its own datastore and wolfSSL FIPS crypto, so there are no external database or crypto packages to install.",
      "Download and verify both matching RPMs first (see the two questions above). Import the signing key, then let dnf resolve OS dependencies together: sudo dnf install ./pumamesh-portal-*.avx256.el10.x86_64.rpm ./pumamesh-portal-selinux-*.avx256.el10.noarch.rpm.",
      "Enable and start the agent with sudo systemctl enable --now pumamesh-agent, then run sudo /opt/pumamesh/bin/pumamesh-wizard to set up identity, certificates, and your first peer. Confirm it is healthy with /opt/pumamesh/bin/pumamesh status. Everything installs under /opt/pumamesh. If something goes wrong, grab the logs and open a case from the Tickets tab.",
    ],
  },
  {
    id: "why-no-builds",
    question: "Why don't I see the build or release line I expect?",
    answer: [
      "The Builds tab shows only entitled release-avx256-rl10 tarballs and release-rpm-el10 complete portal + SELinux policy RPM pairs. If either line is missing, your account may not be entitled or the build may not be promoted; a lone RPM is never downloadable.",
      "If the Builds tab is empty but you expect access, your login may not be associated with the right account yet. Open a case from the Tickets tab and a PumaMesh admin can adjust your account's entitlements or membership.",
    ],
  },
  {
    id: "add-teammate",
    question: "How do I add a teammate to my account?",
    answer: [
      "Your teammate registers at portal.pumamesh.com with their work email. A PumaMesh admin then approves them and associates them with your account.",
      "On approval they receive an email to set a password (or they can use Google sign-in with the same address). Use Forgot password on the sign-in page any time to get a fresh set-password link.",
      "Support cases are shared across your whole account, so any case opened by a teammate is visible to everyone on the account and work does not stall when someone is out. To add or remove a teammate, open a case from the Tickets tab.",
    ],
  },
  {
    id: "upload-logs",
    question: "How do I share logs with support?",
    answer: [
      "The best way is to attach them directly to a case from the Tickets tab, so the logs and the conversation stay together. You can also upload from the Log uploads tab to stage large captures and reference them later.",
      "Agent logs live under /opt/pumamesh/logs; to capture recent service output run sudo journalctl -u pumamesh-agent --since \"-2h\" > pumamesh-agent.log. Reproduce the issue first so the entries are recent, and include the exact build and OS in the case.",
      "Everything you upload stays in your account's isolated, US-hosted storage. Attachments are malware-scanned before support can download them. Never paste secrets or credentials into a case body.",
    ],
  },
  {
    id: "data-protection",
    question: "Is my data safe? How is CUI handled?",
    answer: [
      "Case content, comments, attachments, and logs are stored in PumaMesh-owned, US-hosted storage on Google Cloud (us-central1), with each account in its own isolated bucket so your data is never commingled with another customer's. Data is encrypted in transit and at rest, downloads use short-lived signed links, and administrative actions are audited.",
      "Where the portal connects to outside services it sends only minimal, non-CUI metadata: HubSpot receives only an opaque case shadow (id, account, status/priority/category, link back) — never the subject, body, comments, or attachments.",
      "The portal's handling of your information is built to align with CMMC and NIST SP 800-171. Full compliance datasheets are available on the Documents tab.",
    ],
  },
  {
    id: "more-help",
    question: "I still need help — what do I do?",
    answer: [
      "Open a case from the Tickets tab. Include the exact build version and OS, describe what you expected versus what happened, and attach any relevant logs.",
      "For a quick, non-sensitive question you can also ask the PumaMesh assistant above or from the chat button in the bottom-right corner of any page.",
    ],
  },
];

/**
 * AskAi — a lightweight Q&A widget backed by the portal assistant over the
 * curated NON-CUI corpus. The asker's own account/user attribution is masked server-side
 * by the decoder ring, so only opaque tokens ever leave the portal. A disabled assistant
 * (or an assistant outage) returns a null answer, which is surfaced as a gentle notice
 * rather than an error — the feature is strictly best-effort. §AI
 */
function AskAi({ api }: { api: Api }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);

  async function ask() {
    const q = question.trim();
    if (q === "" || loading) return;
    setLoading(true);
    setAnswer(null);
    setUnavailable(false);
    try {
      const res = await api.askAi(q);
      if (res.answer === null) setUnavailable(true);
      else setAnswer(res.answer);
    } catch {
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="mb-4 p-6">
      <h2 className="text-base font-semibold text-ink">Ask the PumaMesh assistant</h2>
      <p className="mt-1 text-sm text-ink-muted">
        Get an instant answer to a quick, non-sensitive question about the portal, builds, or
        documentation. For anything account-specific, open a case from the Tickets tab.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          className="input"
          type="text"
          value={question}
          placeholder="e.g. How do I verify a build signature?"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") ask();
          }}
          disabled={loading}
        />
        <button
          className="btn-primary shrink-0"
          onClick={ask}
          disabled={loading || question.trim() === ""}
        >
          {loading ? "Asking…" : "Ask"}
        </button>
      </div>
      {answer !== null && (
        <div className="mt-4 border-t border-line pt-4">
          <p className="whitespace-pre-wrap text-sm text-ink-muted">{answer}</p>
        </div>
      )}
      {unavailable && (
        <p className="mt-4 border-t border-line pt-4 text-sm text-ink-dim">
          The assistant is unavailable right now. Please browse the FAQ below or open a case from
          the Tickets tab.
        </p>
      )}
    </Card>
  );
}

/**
 * Faq — renders the curated FAQ as a list of collapsible disclosures, preceded by the
 * Ask-AI widget. Customer-facing and available to every provisioned user; no account data
 * is fetched (the assistant masks identity server-side).
 */
export function Faq({ api }: { api: Api }) {
  // Deep-link support: when arriving at /app/faq#some-id (e.g. from the pre-case
  // deflection panel), open the matching disclosure and scroll it into view.
  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (id === "") return;
    const el = document.getElementById(id);
    if (el instanceof HTMLDetailsElement) {
      el.open = true;
      el.scrollIntoView({ block: "start" });
    }
  }, []);

  return (
    <div>
      <SectionTitle
        kicker="Help · FAQ"
        title="Frequently asked questions"
        desc="Quick answers to the most common questions about the PumaMesh support portal. Can't find what you need? Open a case from the Tickets tab."
      />

      <AskAi api={api} />

      <div className="grid gap-4">
        {FAQ_ITEMS.map((item) => (
          <Card key={item.id} className="p-0">
            <details id={item.id} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 text-base font-semibold text-ink">
                <span>{item.question}</span>
                <span className="shrink-0 text-ink-dim transition group-open:rotate-180" aria-hidden="true">
                  ▾
                </span>
              </summary>
              <div className="border-t border-line px-6 py-4">
                {item.answer.map((p, i) => (
                  <p key={i} className={`text-sm text-ink-muted ${i > 0 ? "mt-3" : ""}`}>
                    {p}
                  </p>
                ))}
              </div>
            </details>
          </Card>
        ))}
      </div>
    </div>
  );
}
